/**
 * What the request choose screen shows for one request and one wallet (Entity Selection First, the
 * 617 request choose screen). Pure, so every rule is unit-tested. The screen only displays it:
 * person-api judges the confirmation and the grant.
 */
import {
  toNewIdentityId,
  type IdentityTypeRowIdentity,
  type RequestIdentityState,
  type RequestPrimaryAction,
  type TEntityChoice,
  type TSelectableEntityType,
} from '@verifime/components';
import { fitsCriteria, permittedEntityTypes, type TRequestCriteria } from '../requestCriteria';

/**
 * A wallet identity as the model reads it: person-api's `entityType` and `entityStatus` as they
 * arrive (plain strings), with the identity's own id and name.
 */
export type TRequestChooseIdentity = {
  id: string;
  name: string;
  entityType: string;
  entityStatus: string;
};

/** One row of the screen: an entity type the customer holds, with its identities in card order. */
export type TRequestChooseHeldType = {
  entityType: TSelectableEntityType;
  identities: IdentityTypeRowIdentity[];
};

export type TRequestChooseModelInput = {
  criteria: TRequestCriteria;
  /** Every identity in the customer's wallet. The model picks the ones that fit. */
  identities: readonly TRequestChooseIdentity[];
  /** The ids of the identities that already hold an Active permission for this client. */
  sharedIdentityIds: ReadonlySet<string>;
  /**
   * True when the screen offers "just me" (verifime-adr#709 Q1). The caller decides it; the model
   * only accepts the choice when it is offered, and never preselects it.
   */
  offersJustMe: boolean;
  /**
   * The id of the option the customer chose, or null before they chose. An identity's id, the
   * new-one id (`new:<entityType>`), or `individual` for just me. An id the screen does not offer,
   * or a shared identity, counts as no choice.
   */
  chosenId: string | null;
};

/**
 * An option the customer can choose: an entity type, and the identity chosen of that type. A new
 * one has no identity yet. Just me is the entity choice `individual`, with no identity: the
 * customer's Individual is the one they already hold, and the confirm screen finds it.
 */
export type TRequestChooseChoice = {
  entityType: TEntityChoice;
  identity?: { id: string; name: string };
};

/** The id of the "just me" option: the Individual entity choice itself. */
const JUST_ME_ID: TEntityChoice = 'individual';

export type TRequestChooseModel = {
  /** Rows: the permitted types the customer holds, in type order, each with its cards. */
  heldTypes: TRequestChooseHeldType[];
  /** Tiles: the permitted types the customer holds none of. */
  unheldTypes: { entityType: TSelectableEntityType }[];
  /** True when the screen offers "just me": the Individual tile, never chosen on arrival. */
  offersJustMe: boolean;
  /** What is chosen before the customer chooses anything, or null when nothing is. */
  arrivedChosenId: string | null;
  /** What is chosen now: the customer's valid choice, else what arrived chosen. */
  selectedId: string | null;
  /** The option behind `selectedId`, or null while nothing is chosen. */
  selected: TRequestChooseChoice | null;
  /** `continue` when something is chosen, else `none`. The choose screen never shares (618 L5). */
  primaryAction: RequestPrimaryAction;
  /**
   * True when the request limits the choice, so the screen explains it in a footer (618 L1). Only
   * an unrestricted request, which is a Legacy request or a DEFAULT invite that declares nothing,
   * limits nothing (#572 Q4b).
   */
  limitsChoice: boolean;
  /**
   * How many identities of a permitted type were left out because their status is none the screen
   * knows. The caller reports it: the customer sees the type as a tile instead.
   */
  hiddenForUnknownStatus: number;
};

type TShownIdentity = IdentityTypeRowIdentity & { entityType: TSelectableEntityType };

// Alphabetical, whatever the case, and the same wherever the page is built.
const BY_NAME = new Intl.Collator('en-AU', { sensitivity: 'base' });

// A Map, not an object: a status such as "constructor" must not find an inherited member.
const STATE_BY_STATUS: ReadonlyMap<string, RequestIdentityState> = new Map([
  ['Verified', 'verified'],
  ['InProgress', 'submitted'],
  ['Rejected', 'submitted'],
  ['Draft', 'draft'],
]);

/**
 * The card state of an identity, or undefined for a status the screen cannot place (such an
 * identity is hidden, and counted in `hiddenForUnknownStatus`).
 * Verified is verified. InProgress and Rejected are submitted (#609 Q6): the identity has gone in
 * and can be shared. Draft is a Draft. An identity with a known status and an Active permission for
 * this client is shared, whatever its status.
 */
function stateOf(
  { id, entityStatus }: TRequestChooseIdentity,
  sharedIdentityIds: ReadonlySet<string>,
): RequestIdentityState | undefined {
  const state = STATE_BY_STATUS.get(entityStatus);
  if (state === undefined) {
    return undefined;
  }
  return sharedIdentityIds.has(id) ? 'shared' : state;
}

/**
 * The entity type of an identity, when it is one of the seven the screen has a row or a tile for.
 * The Individual is not: the customer already holds it, so no row or tile creates a second one
 * (#566 R5). That does not remove "just me", the choice to stay as only the Individual while the
 * entity draft does not exist (#544 Q4): the screen offers it as its own option when the invite
 * allows it (`offersJustMe`, #709 Q1), and it needs no identity of the wallet.
 */
function selectableTypeOf(entityType: string): TSelectableEntityType | undefined {
  return permittedEntityTypes({ kind: 'unrestricted' }).find(
    (selectableType) => selectableType === entityType,
  );
}

type TPlacedIdentities = {
  /** The identities that fit the request and have a row, with a card state, in card order. */
  shown: TShownIdentity[];
  /** How many identities fit and have a row but were hidden because their status is unknown. */
  hiddenForUnknownStatus: number;
};

function placeIdentities({
  criteria,
  identities,
  sharedIdentityIds,
}: TRequestChooseModelInput): TPlacedIdentities {
  const withRow = identities
    .filter((identity) => fitsCriteria(identity.entityType, criteria))
    .flatMap((identity) => {
      const entityType = selectableTypeOf(identity.entityType);
      return entityType ? [{ identity, entityType }] : [];
    });
  const shown = withRow
    .flatMap(({ identity, entityType }) => {
      const state = stateOf(identity, sharedIdentityIds);
      return state ? [{ id: identity.id, name: identity.name, state, entityType }] : [];
    })
    .sort((first, second) => BY_NAME.compare(first.name, second.name));
  return { shown, hiddenForUnknownStatus: withRow.length - shown.length };
}

const isGrantable = ({ state }: IdentityTypeRowIdentity) =>
  state === 'verified' || state === 'submitted';

const choiceOfIdentity = ({ id, name, entityType }: TShownIdentity): TRequestChooseChoice => ({
  entityType,
  identity: { id, name },
});

/** The id the screen knows an option by: an identity's id, or the new-one id of its type. */
const idOfChoice = ({ entityType, identity }: TRequestChooseChoice): string => {
  if (identity) {
    return identity.id;
  }
  return entityType === JUST_ME_ID ? JUST_ME_ID : toNewIdentityId(entityType);
};

/**
 * What arrives chosen (617 Q2b).
 * - The alphabetically first identity that is verified or submitted and not shared.
 * - Else, when everything held is already shared, the new one of the first held identity's type.
 * - Else, when a Draft is held, nothing: the customer finishes it or starts another.
 * - Else, when nothing is held, the new one if the request permits one type only.
 */
function arrivedChoice(
  shown: readonly TShownIdentity[],
  permitted: readonly TSelectableEntityType[],
): TRequestChooseChoice | null {
  const grantable = shown.find(isGrantable);
  if (grantable) {
    return choiceOfIdentity(grantable);
  }
  if (shown.some(({ state }) => state === 'draft')) {
    return null;
  }
  const typeOfNewOne = shown[0]?.entityType ?? (permitted.length === 1 ? permitted[0] : undefined);
  return typeOfNewOne ? { entityType: typeOfNewOne } : null;
}

/** The option the screen offers under this id that the customer may choose, if there is one. */
function choiceOfId(
  id: string,
  shown: readonly TShownIdentity[],
  permitted: readonly TSelectableEntityType[],
  offersJustMe: boolean,
): TRequestChooseChoice | undefined {
  if (id === JUST_ME_ID) {
    return offersJustMe ? { entityType: JUST_ME_ID } : undefined;
  }
  const identity = shown.find((shownIdentity) => shownIdentity.id === id);
  if (identity) {
    return identity.state === 'shared' ? undefined : choiceOfIdentity(identity);
  }
  const entityType = permitted.find((permittedType) => toNewIdentityId(permittedType) === id);
  return entityType ? { entityType } : undefined;
}

export function buildRequestChooseModel(input: TRequestChooseModelInput): TRequestChooseModel {
  const { criteria, chosenId, offersJustMe } = input;
  const permitted = permittedEntityTypes(criteria);
  const { shown, hiddenForUnknownStatus } = placeIdentities(input);

  const heldTypes = permitted.flatMap((entityType) => {
    const identities = shown
      .filter((shownIdentity) => shownIdentity.entityType === entityType)
      .map(({ id, name, state }) => ({ id, name, state }));
    return identities.length > 0 ? [{ entityType, identities }] : [];
  });
  const heldEntityTypes = heldTypes.map((heldType) => heldType.entityType);
  const unheldTypes = permitted
    .filter((entityType) => !heldEntityTypes.includes(entityType))
    .map((entityType) => ({ entityType }));

  const arrived = arrivedChoice(shown, permitted);
  const chosen =
    chosenId === null ? undefined : choiceOfId(chosenId, shown, permitted, offersJustMe);
  const selected = chosen ?? arrived;

  return {
    heldTypes,
    unheldTypes,
    offersJustMe,
    arrivedChosenId: arrived ? idOfChoice(arrived) : null,
    selectedId: selected ? idOfChoice(selected) : null,
    selected,
    primaryAction: selected ? 'continue' : 'none',
    limitsChoice: criteria.kind !== 'unrestricted',
    hiddenForUnknownStatus,
  };
}
